/*
  The Component class provides both data event and custom event management.
*/
import { isEqual } from '../object/isEqual.js';
import { objectApplyDefaults } from '../object/objectApplyDefaults.js';
import { uniqueId } from '../identification/uniqueId.js';
import { has } from '../object/has.js';
import { get } from '../object/get.js';
import { set } from '../object/set.js';
import { Type } from '../type/Type.js';
import { DataPath } from "../data-path/DataPath.js";
import { Data } from "../data/Data.js";
const DATA_REGISTRY_NAMES = ["DATA_CREATE", "DATA_UPDATE", "DATA_CHANGE", "DATA_ACCESS", "DATA_REMOVE"]; // using "as const" here we're able to form a union type out of these array elements as well (so we don't have to redudantly maintain the list in a union type as well)
const ALL_REGISTRY_NAMES = ["DATA_CREATE", "DATA_UPDATE", "DATA_CHANGE", "DATA_ACCESS", "DATA_REMOVE", "CLASS_EVENT"]; // using "as const" here we're able to form a union type out of these array elements as well (so we don't have to redudantly maintain the list in a union type as well)
// this ChildType business will allow us (after some surgery on the Callback type) to resolve the component (sort of: this) type correctly in callbacks
// however, we will need to play a similar genericing came with ChildType on all intermediate dervied classes and probably will just do this on all classes so they are baked as extensible for this reason
// this is brief, so when you come back, if it's not entirely clear, just think about it again :)
export class Component {
    constructor(data = {}, settings = {}) {
        this.eventRegistryCollection = { "DATA_CREATE": {}, "DATA_UPDATE": {}, "DATA_CHANGE": {}, "DATA_ACCESS": {}, "DATA_REMOVE": {}, "CLASS_EVENT": {} }; // the collection of event registries
        this.disabledEventsLookup = { "DATA_CREATE": {}, "DATA_UPDATE": {}, "DATA_CHANGE": {}, "DATA_ACCESS": {}, "DATA_REMOVE": {}, "CLASS_EVENT": {} }; // the disabled events lookup
        this.allRegistrationsLookup = {}; // "all events" lookup by event registration id (facilitates a quick check on a given regisration id's existence)
        this.type = Type.getConstructorName(this);
        this.id = (settings.id !== undefined) ? settings.id : uniqueId(this.type + '-');
        if (Component.InstanceIds.has(this.id)) {
            throw new Error(`Duplicate component id detected: the component id '${this.id}' already exists (component type: '${Component.InstanceIds.get(this.id)}').`);
        }
        Component.InstanceIds.set(this.id, this);
        this.data = new Data(data, {
            throwPathSyntaxInvalidErrors: settings.throwPathSyntaxInvalidErrors,
            throwPathDoesNotExistErrors: settings.throwPathDoesNotExistErrors
        });
    }
    /**
     * Enables debugging (i.e. whether triggerDebugEvent() actually triggers Debug events) with behavior specified by the provided options.
     *
     * @param options options for the debug session
     */
    static enableDebug(options) {
        Component.DebugEnabled = true;
        if (typeof options.componentFilter === 'string') {
            options.componentFilter = new Function('component', options.componentFilter);
        }
        Component.DebugSettings = objectApplyDefaults(options, Component.DebugSettings, true);
    }
    /* Data Functions */
    getType() {
        return this.type;
    }
    getId() {
        return this.id;
    }
    // executes a query while first compiling if necessary
    queryData(query) {
        return this.data.query(query);
    }
    // compiles a query
    compileDataQuery(query) {
        return this.data.compileQuery(query);
    }
    // checks whether a value exists at the specified path
    hasData(path) {
        return this.data.has(path);
    }
    // gets the value at the provided path in data
    getData(path = '', defaultValue = undefined, clone = false) {
        // get the current value from our data object
        const value = this.data.get(path, defaultValue, clone);
        // process data access registrations (callbacks)
        this.triggerDataAccessEvents(path, value);
        // return the value
        return value;
    }
    // sets a value at the specified path in data and fires data change/update events accordingly
    setData(path, newValue, setMode = 'replace', clone = false) {
        // store the old value
        const oldValueClone = this.data.get(path, undefined, true); // get a clone otherwise references in oldValue may still refer to values we update using our call to this.set() below
        // clone the new value if specified
        if (clone) {
            newValue = Type.clone(newValue);
        }
        /*
        * We have a special case to consider because setting paths can actually create intermediate elements; ex. setting 'a.b.c' could create a 'b' object.
        * Thus, we need to check whether the parent 'b' was created before we update / set the new value in data (else we'll lose that information and cannot check in the call to triggerEvent below)
        * To capture this information, we'll check any parent paths in the CREATE registry for existence in our data before setting the new value
        * Then we'll pass an array of the "createdParents" to our DATA_CREATE processor and whether the exact path was created
        */
        const candidateParentPaths = this.getRegistryEventNames('DATA_CREATE');
        const parentPathsCreated = [];
        const N = candidateParentPaths.length;
        for (let i = 0; i < N; i++) {
            const candidateParentPath = candidateParentPaths[i];
            // if we have a parent path which doesn't exist in data manager, then it's going to get created when we set the new path below
            if (DataPath.isParentPath(candidateParentPath, path) && !this.data.has(candidateParentPath)) {
                parentPathsCreated.push(candidateParentPath);
            }
        }
        // if doesn't exist, it'll be created when we "set" below (but we need to compute this here before it exists)
        const created = !this.data.has(path);
        // set the new value into our data object
        this.data.set(path, newValue, setMode);
        // process the registries corresponding to event types which can result from a set operation
        this.triggerDataUpdateEvents(path, oldValueClone, newValue);
        this.triggerDataChangeEvents(path, oldValueClone, newValue, created);
        this.triggerDataRemoveEvents(path, oldValueClone, 'set', newValue);
        this.triggerDataCreateEvents(path, oldValueClone, newValue, created, parentPathsCreated);
    }
    // removes the value at the specified path in data.  note the option to destroy any event registrations
    removeData(path, destroyEvents = false, destroyChildEvents = false) {
        // get the old value (safe to capture this by reference since we're removing it and there is no new value we'll be setting)
        const oldValue = this.data.get(path, undefined);
        // remove the current value from our data object
        const removed = this.data.remove(path);
        // process data REMOVE registrations (callbacks)
        this.triggerDataRemoveEvents(path, oldValue, 'remove');
        // initialize the list of registries for which to potentially destroy events
        let registryNames = [];
        // if destroyEvents is true then destroy the event from any/all registries
        if (destroyEvents === true) {
            // add all data registries to the list
            registryNames = ["DATA_CREATE", "DATA_ACCESS", "DATA_UPDATE", "DATA_CHANGE", "DATA_REMOVE"];
        }
        // if destroyEvents is an array, assume they are the registry names
        else if (Array.isArray(destroyEvents)) {
            // set the registries to be those passed in
            registryNames = destroyEvents;
        }
        // iterate the registry names
        const N = registryNames.length;
        for (let i = 0; i < N; i++) {
            this.destroyEvent(registryNames[i], path, destroyChildEvents);
        }
        return removed;
    }
    /**
     * Performs a walk over the event manager's data object
     *
     * @param handler the handler function or object containing scope, callback, and holdData to call for each node
     * @param direction the direction to walk, either from root downward or leaves upward
     * @param rootPath the path at which to start the walk
     */
    walkData(handler, direction = 'fromRootDownward', rootPath = '') {
        this.data.walk(handler, direction, rootPath);
    }
    hasEventRegistrations(registryName, eventName) {
        if (registryName !== undefined && eventName !== undefined) {
            return this.eventHasRegistrations(registryName, eventName);
        }
        else if (registryName !== undefined) {
            return this.registryHasEvents(registryName);
        }
        else if (eventName !== undefined) {
            const N = ALL_REGISTRY_NAMES.length;
            for (let i = 0; i < N; i++) {
                if (this.eventHasRegistrations(ALL_REGISTRY_NAMES[i], eventName)) {
                    return true;
                }
            }
        }
        else {
            const N = ALL_REGISTRY_NAMES.length;
            for (let i = 0; i < N; i++) {
                if (this.registryHasEvents(ALL_REGISTRY_NAMES[i])) {
                    return true;
                }
            }
        }
    }
    registerForEvent(registryName, eventName, callback, scope, holdData) {
        // generate a unique registration id, prefixed with the registry name and event name for debug clarity
        const registrationId = uniqueId(eventName + ":" + registryName + ":");
        // create the eventName key if it doesn't already exist in this registry
        if (!this.eventRegistryCollection[registryName].hasOwnProperty(eventName)) {
            this.eventRegistryCollection[registryName][eventName] = {};
        }
        // add the specified callback with accompanying scope and holdData on the specified registry's event
        this.eventRegistryCollection[registryName][eventName][registrationId] = { callback: callback, scope: scope, holdData: holdData };
        // add the registryName and eventName to the "all events" lookup
        this.allRegistrationsLookup[registrationId] = { registryName: registryName, eventName: eventName };
        // return the registration id
        return registrationId;
    }
    // unregisters a callback associated with the specified event in the specified registry
    unregisterFromEvent(registrationId) {
        // make sure the registrationId is still valid
        if (!this.eventRegistrationExists(registrationId)) {
            throw new Error("The event registration id '" + registrationId + "' was not found.");
        }
        // get the registryName and eventName corresponding to the supplied registrationId
        const { registryName, eventName } = this.getEventRegistrationInfo(registrationId);
        // remove the registration from under the specified registry's event (making it available for garbage collection)
        delete this.eventRegistryCollection[registryName][eventName][registrationId];
        // if the eventName no longer has any registrations, remove it
        if (Object.keys(this.eventRegistryCollection[registryName][eventName]).length === 0) {
            delete this.eventRegistryCollection[registryName][eventName];
        }
        // remove the registration from the "all events" lookup
        delete this.allRegistrationsLookup[registrationId];
    }
    triggerEvent(registryName, eventName, eventData) {
        // pluck out the registry of interest from our collection and execute callbacks if relevant
        const registry = this.eventRegistryCollection[registryName];
        // if the event exists and is not disabled
        if (this.eventExists(registryName, eventName) && this.isEventEnabled(registryName, eventName)) {
            // if we're not a debug event OR we are and we're enabled and match the debug filter
            if (eventName !== 'Debug' || (eventName === 'Debug' && Component.DebugEnabled && Component.DebugSettings.componentFilter(this))) {
                this.callRegisteredCallbacks(registryName, eventName, eventData);
            }
        }
    }
    // fires an event associated with the specified event in the specified registry
    enableEvent(registryName, eventName) {
        // capture the disabled event info so we can return it
        const disabledEventInfo = this.disabledEventsLookup[registryName][eventName];
        // remove the event from the disabled events lookup
        delete this.disabledEventsLookup[registryName][eventName];
        // return the disabled event info
        return disabledEventInfo;
    }
    // disable the specified event in the specified registry
    disableEvent(registryName, eventName) {
        // add the event name to the disabledEvents lookup; note that we DO allow disabling events even if no registrations exist on that event yet
        this.disabledEventsLookup[registryName][eventName] = { "DisabledAtTime": new Date().getTime() };
    }
    // returns true if the event is currently enabled
    isEventEnabled(registryName, eventName) {
        // return true if we do NOT find the specified event in the specified registry within the disabled events lookup
        return !this.disabledEventsLookup[registryName].hasOwnProperty(eventName);
    }
    // get disabled event information for the specified eventName (which is a data path for data events)
    getDisabledEventInfo(registryName, eventName) {
        // make sure the event is disabled
        if (this.isEventEnabled(registryName, eventName)) {
            throw new Error("The event '" + eventName + "' was not found to be disabled in registry '" + registryName + "'");
        }
        return this.disabledEventsLookup[registryName][eventName];
    }
    // checks if an event exists in the specified registry
    eventExists(registryName, eventName) {
        // return true if the registry has the eventName
        return this.eventRegistryCollection[registryName].hasOwnProperty(eventName);
    }
    // checks if an event registrationId exists
    eventRegistrationExists(registrationId) {
        return this.allRegistrationsLookup.hasOwnProperty(registrationId);
    }
    // checks if an event has any registrations
    eventHasRegistrations(registryName, eventName) {
        // return true if the registry has the eventName AND it has registrations
        return (this.eventRegistryCollection[registryName].hasOwnProperty(eventName) && Object.keys(this.eventRegistryCollection[registryName][eventName]).length > 0);
    }
    // returns the names of the events in the specified registry
    getRegistryEventNames(registryName) {
        return Object.keys(this.eventRegistryCollection[registryName]);
    }
    // returns true if the registry has events, false otherwise
    registryHasEvents(registryName) {
        return this.getRegistryEventNames(registryName).length > 0;
    }
    // gets the event registration information (registryName and eventName) given a registrationId
    getEventRegistrationInfo(registrationId) {
        // make sure the registrationId is still valid
        if (!this.eventRegistrationExists(registrationId)) {
            throw new Error("The event registration id '" + registrationId + "' was not found.");
        }
        return this.allRegistrationsLookup[registrationId];
    }
    // removes an event from an event registry (and therefore all of it's registrations) AND (this is really the destroy part vs just a remove) also removes the event from associated lookups
    destroyEvent(registryName, eventName, destroyChildEvents = false) {
        // pluck out the registry of interest from our collection
        const registry = this.eventRegistryCollection[registryName];
        // switch based on the registry name
        switch (registryName) {
            case "DATA_CREATE":
            case "DATA_UPDATE":
            case "DATA_CHANGE":
            case "DATA_ACCESS":
            case "DATA_REMOVE":
                // process each registeredDataPath to determine whether to clean up the event and its registrations
                for (const registeredDataPath in registry) {
                    // remove if we either have the eventName OR we're removing child events and eventName is a parent of the registeredDataPath
                    if (registeredDataPath === eventName || (destroyChildEvents && DataPath.isParentPath(eventName, registeredDataPath))) {
                        // delete the event and corresponding references in lookups
                        this.cleanupEvent(registryName, registeredDataPath);
                    }
                }
                break;
            case "CLASS_EVENT":
                // if the event exists we'll proceed with its destruction
                if (registry.hasOwnProperty(eventName)) {
                    // delete the event and corresponding references in lookups
                    this.cleanupEvent(registryName, eventName);
                }
                break;
        }
    }
    // clears the registry of all events
    clearRegistry(registryName) {
        let registryNames;
        if (registryName === undefined) {
            registryNames = new Array(...ALL_REGISTRY_NAMES);
        }
        else {
            registryNames = [registryName];
        }
        const N = registryNames.length;
        for (let i = 0; i < N; i++) {
            const registryName = registryNames[i];
            // get the specified registry and destroy all events within it
            const registry = this.eventRegistryCollection[registryName];
            for (const eventName in registry) {
                this.destroyEvent(registryName, eventName);
            }
        }
    }
    destroy(triggerDestroyBeforeEvent = true) {
        if (triggerDestroyBeforeEvent) {
            this.triggerDestroyBeforeEvent();
        }
        this.triggerEvent('CLASS_EVENT', 'Destroy:After', { id: this.id });
        this.clearRegistry();
        Component.InstanceIds.delete(this.id);
    }
    /* Helper Functions */
    /**
     * Triggers the Destroy:Before event.
     * This is a convenience method which can be called by child classes that override destroy() so they don't have to remember the exact signature for this event.
     * This also ensures that if we ever trigger this event with other data, we have a single point of maintenance to make that change - right here :)
     */
    triggerDestroyBeforeEvent() {
        this.triggerEvent('CLASS_EVENT', 'Destroy:Before', { id: this.id });
    }
    // removes the relevant registry and lookup entries
    cleanupEvent(registryName, eventName) {
        // pluck out the registry of interest from our collection
        const registry = this.eventRegistryCollection[registryName];
        // get a reference to the current eventName's registrations
        const registrations = registry[eventName];
        // iterate those registrations (callbacks)
        for (const uniqueId in registrations) {
            // clean up the AllRegistrationsLookup
            delete this.allRegistrationsLookup[uniqueId];
        }
        // remove the event from the disabledEventsLookup (even if it isn't disabled, this is innocuous)
        delete this.disabledEventsLookup[registryName][eventName];
        // remove the event from the specified event registry
        delete registry[eventName];
    }
    // calls the callbacks in specified registry corresponding to the specified eventName (or data path) while passing the specified eventData (calls it in the registered scope and also passes the registered holdData)
    callRegisteredCallbacks(registryName, eventName, eventData) {
        const registrations = this.eventRegistryCollection[registryName][eventName];
        for (const uniqueId in registrations) {
            const registration = registrations[uniqueId];
            registration.callback.call(registration.scope, {
                component: this,
                eventType: registryName,
                eventName: eventName,
                eventData: Type.clone(eventData)
            }, registration.holdData);
        }
    }
    // triggers any data update events corresponding to the specified path, oldValue, and newValue
    triggerDataUpdateEvents(path, oldValue, newValue) {
        const eventData = {
            path: '',
            oldValue: undefined,
            newValue: undefined,
            source: {
                path: path,
                oldValue: oldValue,
                newValue: newValue
            }
        };
        const registry = this.eventRegistryCollection['DATA_UPDATE'];
        for (const registeredDataPath in registry) {
            // if the event is enabled then proceed
            if (this.isEventEnabled('DATA_UPDATE', registeredDataPath)) {
                // if exact path
                if (registeredDataPath === path) {
                    eventData.path = registeredDataPath;
                    eventData.oldValue = oldValue;
                    eventData.newValue = newValue;
                    this.callRegisteredCallbacks('DATA_UPDATE', registeredDataPath, eventData);
                }
                // else if child path
                else if (DataPath.isParentPath(path, registeredDataPath)) {
                    const relativePath = DataPath.getRelativePath(path, registeredDataPath);
                    // only proceed if the child exists in both oldValue and newValue (i.e. we don't fire "change" events for "create" or "remove")
                    if (has(oldValue, relativePath) && has(newValue, relativePath)) {
                        eventData.path = registeredDataPath;
                        eventData.oldValue = get(oldValue, relativePath);
                        eventData.newValue = get(newValue, relativePath);
                        this.callRegisteredCallbacks('DATA_UPDATE', registeredDataPath, eventData);
                    }
                }
                // else if parent path
                else if (DataPath.isParentPath(registeredDataPath, path)) {
                    const newParentValue = this.data.get(registeredDataPath); // don't use this.get() here, else we'll recursively trigger another DATA_ACCESS event
                    const relativePath = DataPath.getRelativePath(registeredDataPath, path);
                    const oldParentValue = set(Type.clone(newParentValue), relativePath, oldValue);
                    eventData.path = registeredDataPath;
                    eventData.oldValue = oldParentValue;
                    eventData.newValue = newParentValue;
                    this.callRegisteredCallbacks('DATA_UPDATE', registeredDataPath, eventData);
                }
            }
        }
    }
    // triggers events corresponding to the specified path, oldValue, and newValue
    triggerDataChangeEvents(path, oldValue, newValue, created) {
        const eventData = {
            path: '',
            oldValue: undefined,
            newValue: undefined,
            source: {
                path: path,
                oldValue: oldValue,
                newValue: newValue
            }
        };
        const registry = this.eventRegistryCollection['DATA_CHANGE'];
        // process each registeredDataPath to determine whether to execute the corresponding callbacks
        if (Object.keys(registry).length > 0) {
            // if a deep equality check reveals a difference, then something changed and we must check all our registrations just in case
            if (!isEqual(oldValue, newValue)) {
                // process each registeredDataPath to determine whether to execute the corresponding callbacks. note: we do not need an "own property" check here since we're processing a raw object (i.e. no prototype inheritance so there are no "parent" properties)
                for (const registeredDataPath in registry) {
                    // if the event is enabled then proceed
                    if (this.isEventEnabled('DATA_CHANGE', registeredDataPath)) {
                        // if exact path and it was not created
                        if (registeredDataPath === path && !created) {
                            eventData.path = registeredDataPath;
                            eventData.oldValue = oldValue;
                            eventData.newValue = newValue;
                            this.callRegisteredCallbacks('DATA_CHANGE', registeredDataPath, eventData);
                        }
                        // else if child path
                        else if (DataPath.isParentPath(path, registeredDataPath)) {
                            const relativePath = DataPath.getRelativePath(path, registeredDataPath);
                            // only proceed if the child exists in both oldValue and newValue (i.e. we don't fire "change" events for "create" or "remove")
                            if (has(oldValue, relativePath) && has(newValue, relativePath)) {
                                const oldChildValue = get(oldValue, relativePath);
                                const newChildValue = get(newValue, relativePath);
                                // if child value changed (we know the parent value changed, but we're checking if THIS particular child value changed)
                                if (!isEqual(oldChildValue, newChildValue)) {
                                    eventData.path = registeredDataPath;
                                    eventData.oldValue = oldChildValue;
                                    eventData.newValue = newChildValue;
                                    this.callRegisteredCallbacks('DATA_CHANGE', registeredDataPath, eventData);
                                }
                            }
                        }
                        // else if parent path
                        else if (DataPath.isParentPath(registeredDataPath, path)) {
                            const newParentValue = this.data.get(registeredDataPath); // don't use this.get() here, else we'll recursively trigger another DATA_ACCESS event
                            const relativePath = DataPath.getRelativePath(registeredDataPath, path);
                            const oldParentValue = set(Type.clone(newParentValue), relativePath, oldValue);
                            eventData.path = registeredDataPath;
                            eventData.oldValue = oldParentValue;
                            eventData.newValue = newParentValue;
                            this.callRegisteredCallbacks('DATA_CHANGE', registeredDataPath, eventData);
                        }
                    }
                }
            }
        }
    }
    // processes data update registrations and triggers events corresponding to the specified path, oldValue, and newValue
    triggerDataRemoveEvents(path, oldValue, operation, newValue) {
        const eventData = {
            path: '',
            oldValue: undefined,
            source: {
                path: path,
                oldValue: oldValue,
                newValue: newValue,
                operation: operation
            }
        };
        const registry = this.eventRegistryCollection['DATA_REMOVE'];
        // process each registeredDataPath to determine whether to execute the corresponding callbacks
        for (const registeredDataPath in registry) {
            // if the event is enabled then proceed
            if (this.isEventEnabled('DATA_REMOVE', registeredDataPath)) {
                // if exact path
                if (registeredDataPath === path) {
                    // if explicit remove
                    if (operation === 'remove') {
                        eventData.path = registeredDataPath;
                        eventData.oldValue = oldValue;
                        this.callRegisteredCallbacks('DATA_REMOVE', registeredDataPath, eventData);
                    }
                }
                // else if child path
                else if (DataPath.isParentPath(path, registeredDataPath)) {
                    const relativePath = DataPath.getRelativePath(path, registeredDataPath);
                    // if was removed
                    if (has(oldValue, relativePath) && !has(newValue, relativePath)) {
                        eventData.path = registeredDataPath;
                        eventData.oldValue = get(oldValue, relativePath);
                        this.callRegisteredCallbacks('DATA_REMOVE', registeredDataPath, eventData);
                    }
                }
            }
        }
    }
    // processes data update registrations and triggers events corresponding to the specified path, oldValue, and newValue
    triggerDataCreateEvents(path, oldValue, newValue, created, parentPathsCreated) {
        const eventData = {
            path: '',
            newValue: undefined,
            source: {
                path: path,
                oldValue: oldValue,
                newValue: newValue,
                created: created,
                parentPathsCreated: parentPathsCreated
            }
        };
        const registry = this.eventRegistryCollection['DATA_CREATE'];
        for (const registeredDataPath in registry) {
            // if the event is enabled then proceed
            if (this.isEventEnabled('DATA_CREATE', registeredDataPath)) {
                // if exact path
                if (registeredDataPath === path) {
                    // if newly created
                    if (created) {
                        eventData.path = registeredDataPath;
                        eventData.newValue = newValue;
                        this.callRegisteredCallbacks('DATA_CREATE', registeredDataPath, eventData);
                    }
                }
                // else if child path
                else if (DataPath.isParentPath(path, registeredDataPath)) {
                    const relativePath = DataPath.getRelativePath(path, registeredDataPath);
                    // if newly created
                    if (!has(oldValue, relativePath) && has(newValue, relativePath)) {
                        eventData.path = registeredDataPath;
                        eventData.newValue = get(newValue, relativePath);
                        this.callRegisteredCallbacks('DATA_CREATE', registeredDataPath, eventData);
                    }
                }
                // else if parent path
                else if (DataPath.isParentPath(registeredDataPath, path)) {
                    // if newly created
                    if (parentPathsCreated.includes(registeredDataPath)) {
                        eventData.path = registeredDataPath;
                        eventData.newValue = this.data.get(registeredDataPath); // don't use this.get() here, else we'll trigger a DATA_ACCESS event
                        this.callRegisteredCallbacks('DATA_CREATE', registeredDataPath, eventData);
                    }
                }
            }
        }
    }
    // processes data update registrations and triggers events corresponding to the specified path, oldValue, and newValue
    triggerDataAccessEvents(path, value) {
        const eventData = {
            path: '',
            value: undefined,
            source: {
                path: path,
                value: value
            }
        };
        const registry = this.eventRegistryCollection['DATA_ACCESS'];
        for (const registeredDataPath in registry) {
            // if the event is enabled then proceed
            if (this.isEventEnabled('DATA_ACCESS', registeredDataPath)) {
                // if exact path
                if (registeredDataPath === path) {
                    eventData.path = registeredDataPath;
                    eventData.value = value;
                    this.callRegisteredCallbacks('DATA_ACCESS', registeredDataPath, eventData);
                }
                // else if child path
                else if (DataPath.isParentPath(path, registeredDataPath)) {
                    const relativePath = DataPath.getRelativePath(path, registeredDataPath);
                    eventData.path = registeredDataPath;
                    eventData.value = get(value, relativePath);
                    this.callRegisteredCallbacks('DATA_ACCESS', registeredDataPath, eventData);
                }
                // else if parent path
                else if (DataPath.isParentPath(registeredDataPath, path)) {
                    eventData.path = registeredDataPath;
                    eventData.value = this.data.get(registeredDataPath);
                    this.callRegisteredCallbacks('DATA_ACCESS', registeredDataPath, eventData);
                }
            }
        }
    }
}
Component.InstanceIds = new Map();
Component.DebugEnabled = false;
Component.DebugSettings = {
    componentFilter: (component) => { return true; }
};
//# sourceMappingURL=Component.js.map